import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

const STATSIG_UPDATE_ERROR = 'Failed to update Statsig user context';

const StatsigUserSynchronizer = ({ statsigUser }) => {
  const { client } = useStatsigClient();
  const prevUserIdRef = useRef(null);
  const pendingUserIdRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const nextUserId = statsigUser?.userID;

    if (!client || !nextUserId || client.isNoop) {
      return;
    }

    if (prevUserIdRef.current === nextUserId || pendingUserIdRef.current === nextUserId) {
      return;
    }

    const updateUser = client.updateUserAsync || client.updateUser || client.updateUserSync;

    if (typeof updateUser !== 'function') {
      return;
    }

    const requestId = ++requestIdRef.current;
    pendingUserIdRef.current = nextUserId;

    const handleError = (error) => {
      if (requestIdRef.current === requestId) {
        pendingUserIdRef.current = null;
      }
      console.error(STATSIG_UPDATE_ERROR, error);
    };

    try {
      const result = updateUser.call(client, statsigUser);

      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            if (requestIdRef.current !== requestId) {
              return;
            }
            prevUserIdRef.current = nextUserId;
            pendingUserIdRef.current = null;
          })
          .catch(handleError);
        return;
      }

      prevUserIdRef.current = nextUserId;
      pendingUserIdRef.current = null;
    } catch (error) {
      handleError(error);
    }
  }, [client, statsigUser]);

  return null;
};

StatsigUserSynchronizer.propTypes = {
  statsigUser: PropTypes.shape({
    custom: PropTypes.object,
    email: PropTypes.string,
    userID: PropTypes.string,
  }),
};

StatsigUserSynchronizer.defaultProps = {
  statsigUser: null,
};

export default StatsigUserSynchronizer;
